import {
    AddProductSurveyRequest,
    type AddProductSurveyRequest_AddSurveyAnswer,
    type ProductSurveyItem,
    ProductSurveyQuestionType,
    type UserProductSurvey_SubscriptionInfo,
} from '@app/_proto/Protos/product_surveys';
import { ProductSurveysClient } from '@app/_proto/Protos/product_surveys.client';
import SurveyAnswerMultiple from '@app/components/survey/survey.answer.multiple';
import SurveyAnswerNumber from '@app/components/survey/survey.answer.number';
import SurveyAnswerText from '@app/components/survey/survey.answer.text';
import { useHttp } from '@app/libs/http';
import { Close } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

export type SurveyDialogProps = {
    survey: ProductSurveyItem;
    productId: number;
    subscriptionInfo?: UserProductSurvey_SubscriptionInfo;
    open: boolean;
    onClose: () => void;
};

export default function SurveyDialog({ survey, productId, subscriptionInfo, open, onClose }: SurveyDialogProps) {
    const { grpcRequest } = useHttp();

    const [answers, setAnswers] = useState<AddProductSurveyRequest_AddSurveyAnswer[]>([]);
    const [isSubmitting, setSubmitting] = useState(false);

    const questions = useMemo(() => {
        return survey?.questions.sort((a, b) => a.order - b.order);
    }, [survey]);

    const handleSubmit = useCallback(async () => {
        if (!survey) {
            return;
        }

        const request = AddProductSurveyRequest.create({
            surveyId: survey.id,
            productId: productId,
            subscriptionInfo: subscriptionInfo,
            answers: answers,
        });

        try {
            setSubmitting(true);
            await grpcRequest(ProductSurveysClient, c => c.addProductSurvey, request);
        } catch {
            //ignore
        }
    }, [survey, answers, productId, subscriptionInfo, grpcRequest]);

    const handleAnswer = (questionId: string, answer: string) => {
        const answersList = [...answers];
        const answerIndex = answersList.findIndex(a => a.questionId === questionId);

        if (answer === '') {
            if (answerIndex > -1) {
                answersList.splice(answerIndex, 1);
                setAnswers(answersList);
            }
            return;
        }

        const newAnswer: AddProductSurveyRequest_AddSurveyAnswer = { questionId: questionId, data: answer };

        if (answerIndex >= 0) {
            answersList[answerIndex] = newAnswer;
            setAnswers(answersList);
        } else {
            answersList.push(newAnswer);
            setAnswers(answersList);
        }
    };

    return (
        <Dialog
            fullWidth
            open={open}
            onClose={onClose}
            PaperProps={{
                className: 'dark:!bg-[#202020] !max-w-none !max-h-none !w-full !h-screen !m-0',
            }}
        >
            {!isSubmitting && (
                <DialogTitle className="flex items-center justify-between py-[20px] pl-[20px] pr-[11px] w-full max-w-lg mx-auto">
                    <span className="text-[18px] font-bold leading-none">Successfully unsubscribed</span>
                    <Close onClick={onClose} className="cursor-pointer text-[#777]" />
                </DialogTitle>
            )}
            <DialogContent className="overflow-hidden p-0">
                <div className="styled-scrollbar w-full max-w-lg mx-auto p-20 pt-0 overflow-auto h-full text-[#666] dark:text-[#BDBDBD]">
                    {isSubmitting ? (
                        <div className="w-full h-full flex flex-col justify-center items-center text-center p-15">
                            <div className="text-[24px] pb-[40px]">Thank you!</div>
                            <div>
                                <Button
                                    className="min-h-[36px] min-w-[94px] !text-[13px] !font-semibold"
                                    variant="outlined"
                                    color="secondary"
                                    onClick={onClose}
                                >
                                    Close
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {survey?.description?.value && (
                                <p className="text-[18px] leading-[24px] font-bold pb-10">{survey.description.value}</p>
                            )}

                            <div className="">
                                {questions.map((question, idx) => (
                                    <div key={question.id} className="mt-[20px]">
                                        <div>{question.question}</div>
                                        {question.description?.value && (
                                            <div className="text-[12px] leading-[21px]">
                                                {question.description.value}
                                            </div>
                                        )}
                                        {question.type === ProductSurveyQuestionType.MultipleChoice && (
                                            <SurveyAnswerMultiple question={question} onAnswer={handleAnswer} />
                                        )}
                                        {question.type === ProductSurveyQuestionType.Text && (
                                            <SurveyAnswerText question={question} onAnswer={handleAnswer} />
                                        )}
                                        {question.type === ProductSurveyQuestionType.Number && (
                                            <SurveyAnswerNumber question={question} onAnswer={handleAnswer} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </DialogContent>
            {!isSubmitting && (
                <DialogActions className="!space-x-[10px] px-[20px] pb-[20px] pt-0 w-full max-w-lg mx-auto">
                    <Button
                        className="min-h-[36px] min-w-[94px] !text-[13px] !font-semibold"
                        variant="outlined"
                        color="secondary"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        className="min-h-[36px] min-w-[94px] !text-[13px] !font-semibold"
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={answers.length === 0}
                    >
                        Submit
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
}
